<template>
  <div
    v-if="$can('set-income-costs', 'YachtModel')"
    style="height: 100%"
  >
    <v-container
      class="py-0"
      fluid
      style="height: 100%"
    >
      <v-row
        no-gutters
        class="mb-6"
        align="center"
      >
        <v-col cols="auto">
          <div class="headline">
            Yacht Booking Pricelist
          </div>
        </v-col>
        <v-spacer />
        <v-col cols="auto">
          <v-btn
            color="primary"
            large
            text
          >
            <v-icon
              left
              small
            >
              fa fa-list
            </v-icon>
            Edit Seasons
          </v-btn>
        </v-col>
        <v-col cols="auto" />
      </v-row>

      <v-row>
        <v-col>
          <v-btn
            v-for="year in years"
            :key="'year_'+year"
            :color="filters.year === year ? 'primary' : undefined"
            large
            :text="filters.year !== year"
            @click="filters.year = year"
          >
            {{ year }}
          </v-btn>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <v-tabs
            v-model="filters.countryId"
            background-color="transparent"
          >
            <v-tab
              v-for="country in countries"
              :key="country.id"
              :href="'#'+country.id"
              class="text-none caption"
            >
              <flag
                :iso="country.code"
                class="mr-1"
                :squared="false"
              />
              {{ country.name }}
            </v-tab>
          </v-tabs>
        </v-col>
        <v-col cols="auto">
          <v-autocomplete
            v-model="filters.yachtTypeId"
            solo
            flat
            background-color="transparent"
            label="Yacht Type"
            :items="yachtTypes"
            hide-details
            item-value="id"
            item-text="label"
          />
        </v-col>
      </v-row>

      <v-divider class="mb-2" />

      <v-card class="pa-0">
        <div
          v-if="yachts.loading || seasons.loading"
          class="pa-6 text-center"
        >
          <v-progress-circular
            :size="50"
            :width="3"
            indeterminate
          />
        </div>
        <div v-else>
          <v-row
            v-if="seasons.data.length > 0"
            no-gutters
            style="border-bottom: 1px solid rgba(0, 0, 0, 0.12)"
            align="center"
          >
            <v-col class="pa-2">
              <v-text-field
                v-model="searchDebounced"
                outlined
                style="max-width: 450px"
                append-icon="search"
                hide-details
                dense
                clearable
                label="Search yacht"
              />
            </v-col>
            <v-col
              cols="auto"
              class="pa-2"
            >
              <v-btn
                icon
                :disabled="seasons.page === 1"
                @click="seasons.page = seasons.page - 1"
              >
                <v-icon small>
                  fa fa-arrow-left
                </v-icon>
              </v-btn>
            </v-col>
            <v-col
              cols="auto"
              class="grey--text text--darken-1"
            >
              {{ pagedSeasons[0].start_date | moment('DD.MM.') }} - {{ pagedSeasons[pagedSeasons.length - 1].end_date | moment('DD.MM.') }}
            </v-col>
            <v-col
              cols="auto"
              class="pa-2"
            >
              <v-btn
                icon
                :disabled="seasons.page >= Math.floor(seasons.data.length / seasons.perPage)"
                @click="seasons.page = seasons.page + 1"
              >
                <v-icon small>
                  fa fa-arrow-right
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-data-table
            fixed-header
            height="calc(100vh - 380px)"
            :loading="yachts.loading"
            :items="filteredYachts"
            :items-per-page="10"
            :footer-props="{ disableItemsPerPage: true }"
          >
            <template v-slot:header>
              <thead>
                <tr>
                  <th style="width: 300px; min-width: 300px; max-width: 300px;">
                    Yacht Model
                  </th>
                  <th
                    v-for="season in pagedSeasons"
                    :key="season.id"
                  >
                    {{ season.start_date | moment('DD.MM.') }} - {{ season.end_date | moment('DD.MM.') }}
                  </th>
                </tr>
              </thead>
            </template>
            <template v-slot:item="{ item: yacht }">
              <tr>
                <td
                  style="width: 300px; min-width: 300px; max-width: 300px;"
                >
                  <div>
                    <router-link
                      :to="{ name: 'yachtDashboard', params: { id: yacht.id }}"
                      target="_blank"
                    >
                      #{{ yacht.id }} {{ yacht.name }}
                    </router-link>
                  </div>
                  <div class="grey--text text--lighten-1 mt-1 caption">
                    <strong>{{ yacht.model.label }}</strong>
                  </div>
                  <div class="grey--text text--lighten-1 mt-1 caption">
                    #{{ yacht.model.id }} &middot;
                    <strong>{{ yacht.model.model_year }}</strong> &middot;
                    <strong class="grey--text">{{ yacht.model.cabin_count_description }}</strong> cabins &middot;
                    <strong class="grey--text">{{ yacht.model.berth_count_description }}</strong> RNOG
                  </div>
                  <div class="grey--text text--lighten-1 mt-1 caption">
                    NauSys: {{ yacht.nausys_ids.join(', ') }}
                  </div>
                  <yacht-model-booking-pricelist-settings :yacht="yacht" />
                </td>
                <td
                  v-for="season in pagedSeasons"
                  :key="season.id + yacht.id"
                  class="pa-0"
                >
                  <yacht-model-booking-pricelist-table-column
                    :pivot-data="yacht.pricelists.find(p => p.pricelist_season_id === season.id)"
                    :pivot-data-dirty="yacht.pricelists.find(p => p.pricelist_season_id === season.id)"
                    @change="(isDirty, pivotData) => updateYachtModelPricelistDirty(yacht.id, season.id, isDirty, pivotData)"
                  />
                </td>
              </tr>
            </template>
          </v-data-table>
        </div>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import YachtModelBookingPricelistTableColumn
  from '@/components/settings/yachtModelBookingPricelist/YachtModelBookingPricelistTableColumn'
import PricelistSeason from '@/models/PricelistSeason'
// eslint-disable-next-line no-unused-vars
import Yacht from '@/models/Yacht'
import YachtModelBookingPricelistSettings
  from '@/components/settings/yachtModelBookingPricelist/YachtModelBookingPricelistSettings.vue'
import debounce from 'lodash/debounce'

export default {
    name: 'YachtModelBookingPricelistIndex',
    components: {
      YachtModelBookingPricelistSettings,
      // eslint-disable-next-line vue/no-unused-components
      YachtModelBookingPricelistTableColumn
    },
    data() {
      return {
        yachtModels: {
          data: [],
          loading: false
        },
        yachts: {
          data: [],
          loading: false
        },
        filters: {
          year: (new Date()).getFullYear(),
          yachtManufacturerId: undefined,
          yachtTypeId: undefined,
          countryId: undefined
        },
        years: [(new Date().getFullYear()) - 3, (new Date().getFullYear()) - 2, new Date().getFullYear() - 1, new Date().getFullYear(), (new Date()).getFullYear() + 1, (new Date()).getFullYear() + 2],
        seasons: {
          loading: false,
          data: [],
          page: 1,
          perPage: 12
        },
        submitting: false,
        changed: [],
        search: ''
      }
    },
    computed: {
      countries() {
        return this.$store.state.settings.countries.data.filter(c => c.chartering)
      },
      yachtTypes() {
        return this.$store.state.settings.yachtTypes.data
      },
      pagedSeasons() {
        return this.seasons.data.slice((this.seasons.page - 1) * this.seasons.perPage, (this.seasons.page * this.seasons.perPage) - 1)
      },
      filteredYachts() {
        if (!this.search) return this.yachts.data

        return this.yachts.data.filter(y => y.name.toLowerCase().includes(this.search))
      },
      searchDebounced: {
        get() {
          return this.search
        },
        set: debounce(function (value) {
          this.search = value
        }, 500)
      }
    },
    watch: {
      yachtTypes(newValue) {
        if (!newValue) return

        this.filters.yachtTypeId = this.yachtTypes[0].id
      },
      countries(newValue) {
        if (!newValue) return

        this.filters.countryId = this.countries[0].id
      },
      'filters.year': function () {
        this.fetchSeasons()
      },
      'filters.yachtTypeId': function () {
        this.fetchYachts()
      },
      'filters.countryId': function () {
        this.fetchSeasons()
        this.fetchYachts()
      }
    },
    created() {
      if (this.countries.length > 0) {
        this.filters.countryId = this.countries[0].id
      }

      if (this.yachtTypes.length > 0) {
        this.filters.yachtTypeId = this.yachtTypes[0].id
      }

      this.fetchSeasons()
      this.fetchYachts()
    },
    methods: {
      async fetchSeasons() {
        if (!this.filters.countryId) return

        this.seasons.loading = true

        try {
          this.seasons.data = await PricelistSeason.api.fetch({
            year: this.filters.year,
            country_id: this.filters.countryId,
            order_by: 'start_date'
          })

          this.seasons.page = 1
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong loading seasons',
            'color': 'error'
          })
          throw e
        } finally {
          this.seasons.loading = false
        }
      },
      async fetchYachts() {
        if (!this.filters.yachtTypeId) return

        this.yachts.loading = true

        try {
          let response = await Yacht.api.fetchYachts({
            status: [Yacht.STATUS_IN_USE, Yacht.STATUS_DELIVERY],
            type: this.filters.yachtTypeId,
            country_id: this.filters.countryId
          })

          // this.yachts.data = this.transformYachtsArray(response)
          this.yachts.data = response
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.yachts.loading = false
        }
      }
    }
  }
</script>

<style lang="css" scoped>
  th {
    position: sticky !important;
    top: 0;
    background: white;
    z-index: 10;
  }
  td, th {
    border-right: 1px solid rgba(0, 0, 0, 0.12);
  }
  td:last-child, th:last-child {
    border-right: none;
  }
</style>
